import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Twitter, MessageCircle, Mail, MapPin, Clock, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import Logo from "@/components/Logo";

const SOCIALS = [
  { key: "facebook", icon: Facebook, testid: "footer-social-facebook" },
  { key: "instagram", icon: Instagram, testid: "footer-social-instagram" },
  { key: "linkedin", icon: Linkedin, testid: "footer-social-linkedin" },
  { key: "youtube", icon: Youtube, testid: "footer-social-youtube" },
  { key: "twitter", icon: Twitter, testid: "footer-social-twitter" },
  { key: "messenger", icon: MessageCircle, testid: "footer-social-messenger" },
];

export default function Footer() {
  const { settings } = useSite();
  const brand = settings?.brand || {};
  const contact = settings?.contact || {};
  const social = settings?.social || {};
  const footer = settings?.footer || {};

  return (
    <footer className="relative border-t border-white/10 bg-[#0B0B14] overflow-hidden" data-testid="site-footer">
      <div className="ambient-glow w-[520px] h-[320px] -bottom-40 left-1/2 -translate-x-1/2 opacity-40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          <div className="lg:col-span-2 pr-0 lg:pr-10">
            <div className="flex items-center gap-3 mb-5">
              <Logo settings={settings} size={42} />
              <span className="font-display font-bold text-lg text-white tracking-tight">{brand.name || "Scalovix"}</span>
            </div>
            <p className="text-sm text-mist leading-relaxed mb-6">{footer.about || ""}</p>
            <div className="flex flex-col gap-2.5 text-sm text-mist">
              {contact.email_primary && (
                <a href={`mailto:${contact.email_primary}`} className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-email-link">
                  <Mail className="h-4 w-4 text-accent-bright" /> {contact.email_primary}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${String(contact.phone).replace(/\s/g, "")}`} className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-phone-link">
                  <Phone className="h-4 w-4 text-accent-bright" /> {contact.phone}
                </a>
              )}
              {contact.address && (
                <span className="flex items-center gap-2.5" data-testid="footer-address">
                  <MapPin className="h-4 w-4 text-accent-bright" /> {contact.address}
                </span>
              )}
              {contact.hours && (
                <span className="flex items-center gap-2.5" data-testid="footer-hours">
                  <Clock className="h-4 w-4 text-accent-bright" /> {contact.hours}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2.5 mt-6">
              {SOCIALS.filter((s) => social[s.key]).map((s) => (
                <a
                  key={s.key}
                  href={social[s.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  className="p-2.5 rounded-full border border-white/10 text-mist hover:text-white hover:border-accent/60 hover:bg-accent/10 transition-all duration-300"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {(footer.columns || []).map((col, ci) => (
            <div key={ci}>
              <h4 className="font-mono-label text-[11px] text-accent-bright mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {(col.links || []).map((l, li) => (
                  <li key={li}>
                    <Link to={l.href || "/"} className="text-sm text-mist hover:text-white transition-colors" data-testid={`footer-link-${ci}-${li}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-subtle" data-testid="footer-copyright">
            © {new Date().getFullYear()} {footer.copyright || brand.name || "Scalovix"}
          </p>
          <div className="flex items-center gap-5 text-xs text-subtle">
            <Link to="/privacy" className="hover:text-mist transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-mist transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
